import LayerSettings, { Layer } from '../../../../models/layer-settings';
import { LayerSettingsHandler } from './layer-settings-reference-handler';

export type LayerRemoveFunction = (layer: Layer) => void;
export type LayerAddFunction = (index: number, layer: Layer) => void;
export type LayerChangeActions = { remove: LayerRemoveFunction, add: LayerAddFunction };

export interface LayerState {
  activeOverlays: () => Layer[];
  layerSettingsInstance: () => LayerSettings;
  onLayerChange: (LayerChangeActions) => void;
  suspend: () => void;
  forceReload: () => void;
  destroy: () => void;
}

const copy = (layers: Layer[]) => JSON.parse(JSON.stringify(layers || [])) as Layer[];

export const debugLog = (...args: any[]) => {
  if (localStorage.getItem('layer-coordination-debugging') === 'true') {
    // This is not necessarily as performant as it could be, but it's only for debugging
    try {
      const mapPluginPath = '/map/plugins/';
      const { stack } = new Error();
      const parts = stack ? stack.split('https://') : [];
      const secondEntry = parts.length > 2 ? parts[2] : '';
      const extensionLength = secondEntry.indexOf('.tsx') > 0 ? 4 : 3;
      const pluginFile = secondEntry.substring(
        secondEntry.indexOf(mapPluginPath) + mapPluginPath.length,
        secondEntry.indexOf('.ts') + extensionLength,
      );

      console.log(`In ${pluginFile}:`, ...args);
    } catch (error) {
      console.error('Failed to parse which file sent the message due to:', error, ...args);
    }
  }
};

/**
 * The LayerChangeCoordinator allows multiple MapPlugins to add/remove Layers to the MapView
 * correctly sorted. This is useful for plugins responding to User selection changes in the
 * LayerPicker. Using the `onLayerChange` function, a plugin need only define the logic for
 * removing or adding a Layer, and then only if the "layer" is of interest to the plugin.
 *
 */
const LayerChangeCoordinator = (layerSettingReferenceHandler: LayerSettingsHandler): LayerState => {
  const removeFns: LayerRemoveFunction[] = [];
  const addFns: LayerAddFunction[] = [];

  let lastActiveOverlays: Layer[] = copy(layerSettingReferenceHandler.getInstance().activeOverlays);
  let suspended = false;

  const onLayersSaved = () => {
    if (suspended) {
      return;
    }

    const currentActiveOverlays = copy(layerSettingReferenceHandler.getInstance().activeOverlays);
    debugLog('LayerChangeCoordinator State:', { current: currentActiveOverlays, last: lastActiveOverlays });

    for (let x = 0; x < lastActiveOverlays.length; x++) {
      const lastLayer = lastActiveOverlays[x];
      debugLog('LayerChangeCoordinator Removing', x, lastLayer);
      removeFns.forEach((fn) => fn(lastLayer));
    }

    for (let x = currentActiveOverlays.length - 1, y = 0; x >= 0; x--, y++) {
      const currentLayer = currentActiveOverlays[x];
      debugLog('LayerChangeCoordinator Adding', y, currentLayer);
      addFns.forEach((fn) => fn(y, currentLayer));
    }

    lastActiveOverlays = currentActiveOverlays;
  };

  layerSettingReferenceHandler.registerListener('layers-saved', onLayersSaved);
  layerSettingReferenceHandler.onUpdate(onLayersSaved);

  return {
    activeOverlays: () => copy(layerSettingReferenceHandler.getInstance().activeOverlays),
    layerSettingsInstance: layerSettingReferenceHandler.getInstance,
    onLayerChange: ({ remove, add }: LayerChangeActions) => {
      removeFns.push(remove);
      addFns.push(add);
    },
    suspend: () => {
      debugLog('LayerChangeCoordinator suspended');
      suspended = true;
    },
    forceReload: () => {
      debugLog('LayerChangeCoordinator resumed');
      suspended = false;
      onLayersSaved();
    },
    destroy: layerSettingReferenceHandler.destroy,
  };
};

export default LayerChangeCoordinator;
